import React from "react";
import { motion } from "framer-motion";
import toast from "react-hot-toast";
import { Brain, Users, Zap, RefreshCw, Square, Sparkles, AlertCircle, Layers, Target } from "lucide-react";
import { useApi } from "../../../hooks/useApi";
import { useAuth } from "../../../contexts/AuthContext";
import { useDashTheme } from "../hooks/useDashTheme";
import { useAiStream } from "../hooks/useAiStream";
import { PageShell } from "../components/PageShell";
import { SectionCard, Button, Badge } from "../components/primitives";
import { EmptyState } from "../components/DataTable";
import { DateRangeTool, presetToRange } from "../components/DateRange";
import { ToolSelect } from "../components/ToolSelect";
import { HowItWorks } from "../components/HowItWorks";
import { OrgIntelligenceRenderer, OrgIntelMetricsStrip } from "../components/OrgIntelligenceRenderer";
import AILimitModal from "../../../components/AILimitModal/AILimitModal";
import AIUsageBanner from "../../../components/AIUsageBanner/AIUsageBanner";
import { OverallIntelligenceModal } from "../components/OverallIntelligenceModal";
import { SERVER_URL } from "../../../utils/serverConfig";

const fmt = (d) => {
  if (!d) return "";
  const dt = new Date(d);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
};

export default function OrgIntelligenceV2() {
  const t = useDashTheme();
  const api = useApi();
  const { user } = useAuth();
  const stream = useAiStream("/api/v1/org-intelligence/metrics-stream");

  const [member, setMember] = React.useState(null);
  // Default to yesterday (legacy parity: today's data is incomplete).
  const [range, setRange] = React.useState(() => presetToRange("yesterday"));
  const [overallOpen, setOverallOpen] = React.useState(false);

  // Access-scoped user list (from saved-summaries) — same access model the
  // org-intelligence backend authorizes (legacy parity, replaces team-members).
  const [accessibleUsers, setAccessibleUsers] = React.useState([]);
  const [loadingUsers, setLoadingUsers] = React.useState(true);
  const [goals, setGoals] = React.useState({}); // { userId: [goals] }

  React.useEffect(() => {
    (async () => {
      setLoadingUsers(true);
      try {
        const token = localStorage.getItem("token");
        const res = await fetch(`${SERVER_URL}/api/v1/saved-summaries?limit=1`, { headers: { Authorization: `Bearer ${token}` } });
        const data = await res.json();
        if (data?.success && data.data?.accessibleUsers) {
          setAccessibleUsers(data.data.accessibleUsers);
          const me = data.data.accessibleUsers.find((u) => u._id === user?._id) || data.data.accessibleUsers[0];
          if (me) setMember((cur) => cur || me._id); // auto-select current user (legacy parity)
        }
      } catch (_) {} finally { setLoadingUsers(false); }
    })();
  }, [user]);

  const members = React.useMemo(() => accessibleUsers.map((u) => ({
    value: u._id || u.id,
    label: `${u.firstName || ""} ${u.lastName || ""}`.trim() || u.email || "Member",
    sub: u.position || u.email,
  })), [accessibleUsers]);

  const selectedMember = members.find((m) => m.value === member);
  const hasContent = stream.metrics != null || stream.report != null;

  // Changing member/range invalidates a previously-streamed report (legacy parity).
  const onChangeMember = (v) => { setMember(v || null); stream.reset?.(); };
  const onChangeRange = (r) => { setRange(r); stream.reset?.(); };

  // Fetch goals for a user (legacy parity: shown alongside the report).
  const fetchUserGoals = React.useCallback(async (userId) => {
    if (!userId) return;
    try {
      const res = await api.get("/api/v1/goals", { params: { userIds: userId } });
      if (res.data?.success) setGoals(res.data.data || {});
    } catch (_) { /* non-blocking */ }
  }, [api]);

  const generate = (force = false) => {
    if (!member) { toast.error("Please select a user and date range"); return; }
    setGoals({});
    stream.run({ userId: member, startDate: fmt(range.start), endDate: fmt(range.end), forceRegenerate: force });
  };

  // When a stream finishes (text present, not streaming), pull the member's goals.
  React.useEffect(() => {
    if (member && hasContent && !stream.streaming) fetchUserGoals(member);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stream.streaming]);

  const memberGoals = goals[member] || [];

  return (
    <PageShell
      eyebrow="AI · Organizational intelligence"
      title="Org Intelligence"
      subtitle="AI surfaces issues, risks and improvement ideas across your team"
      icon={<Brain size={22} />}
      topRight={
        <div className="tg-filterbar">
          <ToolSelect
            icon={<Users size={13} />}
            value={member}
            options={members}
            onChange={onChangeMember}
            placeholder={loadingUsers ? "Loading users…" : `Select a member (${members.length} accessible)`}
            panelWidth={260}
            placement="bottomRight"
          />
          <span className="tg-tool-sep" />
          <DateRangeTool range={range} onChange={onChangeRange} layoutId="orgRangeChip"
            presets={[
              { key: "yesterday", label: "Yesterday" },
              { key: "7d", label: "7 days" },
              { key: "30d", label: "30 days" },
              { key: "month", label: "This month" },
            ]}
            maxDate={fmt(new Date(Date.now() - 86400000))}
          />
        </div>
      }
      actions={
        stream.streaming ? (
          <Button variant="secondary" leftIcon={<Square size={14} />} onClick={stream.stop}>Stop</Button>
        ) : (
          <>
            {/* Org-wide analysis across all accessible members (legacy "Overall Intelligence") */}
            {/* <Button variant="secondary" leftIcon={<Layers size={15} />} onClick={() => setOverallOpen(true)}>Overall intelligence</Button> */}
            {hasContent && <Button variant="secondary" leftIcon={<RefreshCw size={15} />} onClick={() => generate(true)} disabled={!member}>Regenerate</Button>}
            <Button variant="primary" leftIcon={<Zap size={15} />} onClick={() => generate(false)} disabled={!member}>Run analysis</Button>
          </>
        )
      }
    >
      <AIUsageBanner />

      {/* How it works — shown until a member is picked */}
      {!member ? (
        <HowItWorks
          icon={<Brain size={18} />}
          steps={[
            "Select a team member from the dropdown (based on your access level)",
            "Choose a date range to analyze their work activities",
            "Click “Run analysis” for an AI-powered intelligence report",
          ]}
          bullets={[
            "What work was accomplished",
            "Issues and blockers encountered",
            "Performance improvements & recommendations",
            "Organization-wide insights and patterns",
          ]}
          tip="Intelligence reports are cached for instant loading. Use “Regenerate” to create a fresh analysis."
        />
      ) : (
      /* Output */
      <SectionCard
        title={selectedMember ? `Intelligence · ${selectedMember.label}` : "Intelligence report"}
        subtitle={`${new Date(range.start).toLocaleDateString("en-US", { month: "short", day: "numeric" })} – ${new Date(range.end).toLocaleDateString("en-US", { month: "short", day: "numeric" })}`}
        icon={<Brain size={16} />}
        actions={stream.cached ? <span style={{ fontSize: t.font.size.xs, color: t.c.textTertiary, display: "inline-flex", alignItems: "center", gap: 4 }}><Zap size={12} /> cached</span> : null}
      >
        {stream.error ? (
          <EmptyState icon={<AlertCircle size={22} />} title="No activity found" description={(stream.error || "").replace(/\*\*/g, "")} />
        ) : !hasContent && stream.streaming ? (
          <StreamingLoader t={t} />
        ) : !hasContent ? (
          <EmptyState icon={<Brain size={22} />} title="Ready to analyze"
            description="Run the analysis to surface issues and improvement ideas for this member."
            action={<Button variant="primary" leftIcon={<Zap size={15} />} onClick={() => generate(false)}>Run analysis</Button>} />
        ) : (
          <div>
            {stream.streaming && (
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: t.s[4], fontSize: t.font.size.sm, color: t.c.primary }}>
                <motion.span animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: "linear" }} style={{ display: "inline-flex" }}><Sparkles size={14} /></motion.span>
                Analyzing…
              </div>
            )}
            {/* ① Instant metrics strip (from `metrics` event) */}
            <OrgIntelMetricsStrip metrics={stream.metrics} streaming={stream.streaming && !stream.report} />
            {/* ② Progressive structured report (issues + recommendations + patterns) */}
            {stream.report && (
              <OrgIntelligenceRenderer report={stream.report} streaming={stream.streaming} partial={stream.reportPartial} />
            )}

            {/* Member goals (legacy parity) — context alongside the report */}
            {!stream.streaming && memberGoals.length > 0 && (
              <div style={{ marginTop: t.s[5] }}>
                <div style={{ position: "relative", borderRadius: t.r.lg, border: `1px solid ${t.c.border}`, background: t.c.surface, overflow: "hidden" }}>
                  <div style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 3, background: t.accents[5] }} />
                  <div style={{ padding: `${t.s[4]}px ${t.s[5]}px`, display: "flex", alignItems: "center", gap: 10, borderBottom: `1px solid ${t.c.border}` }}>
                    <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 30, height: 30, borderRadius: 8, background: t.accents[5] + "1A", color: t.accents[5] }}><Target size={16} /></span>
                    <span style={{ fontSize: t.font.size.md, fontWeight: 700, color: t.c.text, letterSpacing: "-0.01em" }}>Goals</span>
                    <span style={{ marginLeft: "auto", fontSize: t.font.size.xs, fontWeight: 600, color: t.c.textTertiary, padding: "2px 9px", borderRadius: 99, background: t.c.surfaceMuted }}>{memberGoals.length}</span>
                  </div>
                  <div style={{ padding: t.s[4], display: "flex", flexDirection: "column", gap: t.s[2] }}>
                    {memberGoals.map((g, i) => {
                      const tone = g.status === "APPROVED" ? "positive" : g.status === "PENDING" ? "warning" : "neutral";
                      return (
                        <div key={g._id || i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, padding: `${t.s[3]}px ${t.s[3]}px`, borderRadius: t.r.md, background: t.c.surfaceMuted, border: `1px solid ${t.c.border}` }}>
                          <span style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                            <span style={{ width: 7, height: 7, borderRadius: 99, background: tone === "positive" ? t.status.positive : tone === "warning" ? t.status.warning : t.c.textTertiary, flexShrink: 0 }} />
                            <span style={{ fontSize: t.font.size.sm, color: t.c.text, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{g.title || g.name || g.goalTitle || "Goal"}</span>
                          </span>
                          {g.status && <Badge tone={tone}>{g.status}</Badge>}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </SectionCard>
      )}

      {/* Overall (org-wide) intelligence modal — legacy parity */}
      <OverallIntelligenceModal
        open={overallOpen}
        onClose={() => setOverallOpen(false)}
        dateRange={{ startDate: fmt(range.start), endDate: fmt(range.end) }}
        onLimitReached={() => stream.setLimitReached(true)}
      />

      <AILimitModal isOpen={stream.limitReached} onClose={() => stream.setLimitReached(false)} />
    </PageShell>
  );
}

function StreamingLoader({ t }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: `${t.s[9]}px ${t.s[5]}px`, gap: t.s[3] }}>
      <motion.span animate={{ scale: [1, 1.15, 1], opacity: [0.7, 1, 0.7] }} transition={{ duration: 1.4, repeat: Infinity }}
        style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 52, height: 52, borderRadius: 16, background: t.c.primaryGradient, color: "#fff", boxShadow: `0 8px 24px ${t.c.glow}` }}>
        <Brain size={24} />
      </motion.span>
      <div style={{ fontSize: t.font.size.lg, fontWeight: 600, color: t.c.text }}>Running intelligence analysis…</div>
      <div style={{ fontSize: t.font.size.sm, color: t.c.textTertiary }}>Scanning activity for issues & ideas</div>
    </div>
  );
}
